<template>
  <v-row>
    <v-col cols="12">
      <v-row class="text-center" align="center" justify="center">
        <v-col cols="12" class="py-0 font-weight-bold">
          Time Signature
        </v-col>
        <v-col cols="12" class="pa-0">
          <app-drop-down-menu :options="beatLengths"
                              :currently-selected="numerator"
                              setting="numerator"
                              :small="$vuetify.breakpoint.smAndDown"
                              :with-icon="false"
                              :rounded="true"
                              class="pa-0"
                              color="primary"
                              style="max-width: 50px; display: inline-block"
                              @updated="setState"/>
          <app-drop-down-menu :options="noteDurations"
                              :currently-selected="denominator"
                              setting="denominator"
                              :small="$vuetify.breakpoint.smAndDown"
                              :with-icon="false"
                              :rounded="true"
                              class="pa-0"
                              color="primary"
                              style="max-width: 50px; display: inline-block"
                              @updated="setState"/>
          <!--      <v-text-field outlined dense v-model="timeSig[0]" />-->
          <!--      <v-text-field outlined dense v-model="timeSig[1]" class="pa-0" style="max-width: 50px; display: inline-block"/>-->
        </v-col>
        <v-col cols="12" class="">
          <v-btn large :block="$vuetify.breakpoint.xsOnly" :color="getIsTransportStarted ? 'error' : 'success'"
                 @click="toggle">
            {{ isPlaying }}
            <app-icon :icon="isPlayingIcon"/>
          </v-btn>
        </v-col>
        <v-col cols="12" class="font-weight-black"
               style="font-size: 25vh; cursor: pointer"
               @click="toggle">
          {{ getBeat }}
        </v-col>
        <v-col cols="12">
          <app-slider thumb-label="always" value-name="BPM" :initial-value="getTransportBPM" :min="40" :max="250"
                      @valueChanged="bpmUpdate"/>
        </v-col>
        <v-col cols="4">
          <v-row no-gutters>
            <v-col cols="12" class="py-5">
              Down Beat
            </v-col>
            <v-col cols="12">
              <app-drop-down-menu setting="downBeat"
                                  :options="drumSounds"
                                  :currently-selected="downBeat"
                                  @updated="setState"
                                  :x-small="$vuetify.breakpoint.xsOnly"/>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="4">
          <v-row no-gutters>
            <v-col cols="12">
              <v-checkbox v-model="isFirstDownBeat" label="Accent"
                          color="success"
                          style="display: inline-block"
                          @change="restart"/>
            </v-col>
            <v-col cols="12" v-if="isFirstDownBeat">
              <app-drop-down-menu setting="firstDownBeat"
                                  :options="drumSounds"
                                  :currently-selected="firstDownBeat"
                                  @updated="setState"
                                  :x-small="$vuetify.breakpoint.xsOnly"/>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="4">
          <v-row no-gutters>
            <v-col cols="12">
              <v-checkbox v-model="isUpBeat" label="Up Beat"
                          color="success"
                          style="display: inline-block"
                          @change="restart"/>
            </v-col>
            <v-col cols="12" v-if="isUpBeat">
              <app-drop-down-menu setting="upBeat"
                                  :options="drumSounds"
                                  :currently-selected="upBeat"
                                  @updated="setState"
                                  :x-small="$vuetify.breakpoint.xsOnly"/>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="4">
          <v-checkbox v-model="isTriplets" label="Triplets"
                      color="success"
                      style="display: inline-block"
                      @change="restart"/>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
  import { mapActions, mapGetters }                        from 'vuex'
  import { Transport }                                     from 'tone'
  import { beatLengths, DRUMS, drumSounds, noteDurations } from 'misc/Constants'
  import AppSlider                                         from 'components/AppSlider'
  import AppDropDownMenu                                   from 'components/AppDropDownMenu'
  import { setTransportTimeSignature, stopTransport }      from 'helpers/tone-helpers'

  let audioManager

  export default {
    name:       'MetronomeClock',
    components: { AppDropDownMenu, AppSlider },
    async created() {
      const module = await import(/* webpackChunkName: "audio-manager" */ 'models/AudioManager')
      audioManager = module.default
      await audioManager.add(DRUMS)
      this.$store.dispatch('visualization/setDrumMatrix')
      Transport.loopEnd = '1m'
      Transport.loop    = false
    },

    destroyed() {
      setTransportTimeSignature(4)
      stopTransport()
    },

    data() {
      return {
        firstDownBeat:   'kick',
        isFirstDownBeat: true,
        downBeat:        'hihat-closed',
        isUpBeat:        false,
        upBeat:          'ride',
        numerator:       4,
        denominator:     4,
        isTriplets:      false,
        drumSounds,
        noteDurations,
        beatLengths,
      }
    },

    computed: {
      ...mapGetters('transport', ['getTransportBPM', 'getPlayerProgress', 'getIsTransportStarted', 'getBeat']),
      beat:          function() {
        const progress = this.getPlayerProgress('drums')
        return progress?.i >= 0 ? progress.i + 1 : 1
      },
      isPlaying:     function() {
        return this.getIsTransportStarted ? 'Stop' : 'Start'
      },
      isPlayingIcon: function() {
        return this.getIsTransportStarted ? 'stop' : 'play-arrow'
      },
    },

    methods: {
      ...mapActions('transport', ['scheduleMetronome', 'toggleTransport', 'setBPM']),
      ...mapActions('visualization', ['setIndicator']),
      async toggle() {
        const config = {
          numerator:     this.numerator,
          denominator:   this.denominator,
          firstDownBeat: this.isFirstDownBeat ? this.firstDownBeat : this.downBeat,
          downBeat:      this.downBeat,
          upBeat:        this.isUpBeat ? this.upBeat : null,
          isTriplets:    this.isTriplets,
        }
        await this.scheduleMetronome(config)
        this.toggleTransport()
      },

      setState({ setting, value }) {
        this[setting] = value
        this.restart()
      },

      restart() {
        if(this.getIsTransportStarted) {
          this.toggleTransport()
          setTimeout(() => this.toggle(), 50)
        }
      },

      bpmUpdate(val) {
        this.setBPM(val)
        this.restart()
      },
    },
  }
</script>
